<script setup lang="ts">
import { Head, Link, useForm } from '@inertiajs/vue3'
import { ref } from 'vue'

import logo from '@/assets/logo-forum-dark.svg'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Title } from '@/components/ui/title'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import AuthLayout from '@/layouts/AuthLayout.vue'
import { CircleHelp, Loader2, MailCheck } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const passwordResetUrl = import.meta.env.VITE_CAS_PASSWORD_RESET_URL ?? '/password_resets/new'

const form = useForm({
    email: '',
    password: '',
})

const resendProcessing = ref(false)
const feedback = ref('')

async function resendConfirmation(email: string) {
    const response = await fetch('/api/v1/email_confirmations/resend', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
        },
        body: JSON.stringify({ email }),
    })

    const data = await response.json()
    return { status: response.status, ...data } as { status: number; sent: boolean; message: string }
}

async function handleResendConfirmation() {
    feedback.value = ''

    resendProcessing.value = true

    try {
        const result = await resendConfirmation(form.email)
        feedback.value = result.message || ''
    } catch {
        feedback.value = ''
    } finally {
        resendProcessing.value = false
    }
}

defineProps<{
    please_confirm_your_email: boolean
}>()

</script>

<template>
    <Head title="Login" />
    <AuthLayout>
        <div class="flex items-center justify-center">
            <img :src="logo" alt="" class="pr-4" width="150" />
        </div>
        <div class="mt-8 w-full">
            <div
                v-if="please_confirm_your_email"
                class="border-primary/25 bg-primary/5 mb-6 flex gap-4 rounded-lg border p-4 text-sm"
                role="status"
            >
                <MailCheck class="text-primary mt-0.5 size-9 shrink-0" aria-hidden="true" />
                <div class="space-y-2">
                    <p class="text-foreground font-semibold">{{ $t('Confirm your email') }}</p>
                    <p class="text-foreground leading-relaxed">
                        {{ $t('Registration completed successfully. We sent an activation email to your account. Please click confirm in the email, then sign in below.') }}
                    </p>
                </div>
            </div>
            <Title>{{ t('Access your account') }}</Title>

            <p class="mt-3 text-sm font-medium">
                {{ $t('Log in with your ILOS username, the same one you use to access ILOS Prime or ILOS Courses') }}
            </p>

            <p class="mt-3 text-sm font-medium">
                {{ $t('Do not have an account?') }}
                <Link href="/users/sign_up" class="font-inter text-primary self-end font-semibold hover:underline">{{ $t('Register') }}</Link>
            </p>

            <form
                @submit.prevent="
                    form
                        .transform((data) => ({
                            user: {
                                ...data,
                            },
                        }))
                        .post('/login')
                "
                id="sign-in"
                class="mt-4 flex flex-col gap-4"
            >
                <div>
                    <Label for="email">{{ $t('Email') }}</Label>
                    <Input id="email" type="text" autocomplete="email" :placeholder="$t('Type your e-mail')" v-model="form.email" />
                    <p class="text-destructive text-xs mt-2" v-if="form.errors.email">{{ form.errors.email }}</p>
                    <button
                        v-if="(form.errors as Record<string, unknown>).code === 'email_not_confirmed'"
                        type="button"
                        class="text-destructive mt-1 text-xs font-semibold underline cursor-pointer "
                        :disabled="resendProcessing"
                        @click="handleResendConfirmation"
                    >
                        {{ $t('Click here to resend the confirmation email') }}
                    </button>
                    <p v-if="feedback" class="mt-1 text-xs">
                        {{ feedback }}
                    </p>
                </div>
                <div>
                    <div class="mb-1 flex items-center gap-x-1">
                        <Label for="password">{{ $t('Password / Access code') }}</Label>
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger aria-label="{{ $t('The access code was sent in the email with the event ticket') }}">
                                    <CircleHelp class="size-4" />
                                </TooltipTrigger>
                                <TooltipContent>
                                    <div class="max-w-64 text-center">
                                        {{ $t('The access code was sent in the email with the event ticket') }}
                                    </div>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </div>
                    <Input id="password" type="password" autocomplete="password" v-model="form.password" placeholder="*******" />
                    <div class="mt-2 flex w-full justify-between">
                        <div>
                            <p class="text-destructive text-xs" v-if="form.errors.password">{{ $t(form.errors.password) }}</p>
                        </div>

                        <a :href="passwordResetUrl" target="_blank" class="font-inter text-primary self-end text-sm font-semibold hover:underline">
                            {{ $t('Forgot my password') }}
                        </a>
                    </div>
                </div>

                <Button :disabled="form.processing">
                    <template v-if="!form.processing">
                        {{ $t('Enter') }}
                    </template>
                    <template v-if="form.processing">
                        <span>
                            <Loader2 class="animate-spin" />
                            <span class="sr-only">{{ $t('Entering') }}</span>
                        </span>
                    </template>
                </Button>
            </form>
        </div>
    </AuthLayout>
</template>
